import { FormattedMessage } from "react-intl";
import Col from "reactstrap/lib/Col";
import Row from "reactstrap/lib/Row";
import Table from "reactstrap/lib/Table";
import {
  AnalysisTabs,
  BarRow,
  DayBars,
  FindingList,
  PanelCard,
  StatCard,
  findingsForTab,
  formatNumber,
} from "./DailyAnalysisParts";

// The table sorts on affected users, not on count, and that is the whole point: an error
// hitting a thousand people once each is a bad school network, while one hitting eight
// people seventeen times each means those eight cannot use the feature at all.
function DailyAnalysisClient({ data }) {
  const summary = data.summary || {};
  const patterns = data.clientErrorPatterns || [];
  const trends = data.trends || {};
  const byDay = trends.clientErrorsByDay || [];
  const byVersion = trends.clientErrorsByVersion || [];
  const findings = findingsForTab(data, AnalysisTabs.CLIENT);

  const maxVersion = Math.max(...byVersion.map((v) => v.count), 0);
  // Read the tag rather than recomputing it from the ratio. An extension error can sit at
  // 16,8 per person and still not be ours - the generator already knows that and tags it
  // Tillägg, which leaves it uncoloured here.
  const verdictTone = (row) =>
    row.tag === "Bugg" ? "bug" : row.tag === "Undersök" ? "check" : "";
  const worstVersion = byVersion.length > 0 ? byVersion[0] : null;

  return (
    <div>
      <Row className="da-equal">
        <Col md="3" sm="6">
          <StatCard
            labelId="DailyAnalysisClientErrors"
            value={formatNumber(summary.clientErrors)}
            note={data.date}
          />
        </Col>
        <Col md="3" sm="6">
          <StatCard
            labelId="DailyAnalysisAffected"
            value={formatNumber(summary.affectedUsers)}
          />
        </Col>
        <Col md="3" sm="6">
          <StatCard
            labelId="DailyAnalysisPatterns"
            value={formatNumber(patterns.length)}
          />
        </Col>
        <Col md="3" sm="6">
          <StatCard
            labelId="Version"
            value={worstVersion ? worstVersion.version : "—"}
            note={
              worstVersion ? (
                <FormattedMessage
                  id="DailyAnalysisErrorsCount"
                  values={{ count: formatNumber(worstVersion.count) }}
                />
              ) : (
                ""
              )
            }
          />
        </Col>
      </Row>

      <Row className="da-equal">
        <Col lg="6">
          <PanelCard titleId="DailyAnalysisPerDay">
            <DayBars
              points={byDay.map((d) => ({
                label: d.date.slice(8),
                count: d.count,
              }))}
            />
          </PanelCard>
        </Col>
        <Col lg="6">
          <PanelCard titleId="DailyAnalysisPerVersion">
            {byVersion.map((row) => (
              <BarRow
                key={row.version}
                label={row.version}
                value={row.count}
                max={maxVersion}
              />
            ))}
          </PanelCard>
        </Col>
      </Row>

      <h6 className="da-section-title">
        <FormattedMessage id="DailyAnalysisPatterns" />
      </h6>
      {/* Which rows appear is decided by the day; how bad each one is comes from the
          window. A single day puts almost every ratio at 1.0, so the verdict column would
          say nothing. The two count columns therefore span different periods, which is
          what the subtitle and the column headers have to make obvious. */}
      <p className="text-muted small">
        <FormattedMessage
          id="DailyAnalysisPatternsSub"
          values={{ days: data.clientErrorPatternDays }}
        />
      </p>
      <Table responsive size="sm" className="da-table">
        <thead>
          <tr>
            <th>
              <FormattedMessage id="DailyAnalysisMessage" />
            </th>
            <th className="da-num">
              <FormattedMessage id="DailyAnalysisOnSelectedDay" />
            </th>
            <th className="da-num">
              <FormattedMessage id="DailyAnalysisAffected" />
            </th>
            <th className="da-num">
              <FormattedMessage id="DailyAnalysisCount" />
            </th>
            <th className="da-num">
              <FormattedMessage id="DailyAnalysisPerPerson" />
            </th>
            <th>
              <FormattedMessage id="DailyAnalysisVerdict" />
            </th>
          </tr>
        </thead>
        <tbody>
          {patterns.map((row, i) => {
            const tone = verdictTone(row);
            return (
              <tr key={`${row.message}|${row.where || i}`}>
                <td className="da-mono">
                  <div
                    className="da-clamp"
                    title={
                      row.where ? row.message + "\n" + row.where : row.message
                    }
                  >
                    {row.message}
                  </div>
                </td>
                <td className="da-num da-num--strong">
                  {formatNumber(row.today)}
                </td>
                <td className="da-num">{formatNumber(row.affected)}</td>
                <td className="da-num">{formatNumber(row.count)}</td>
                <td
                  className={
                    tone === "bug"
                      ? "da-num da-num--strong da-num--crit"
                      : "da-num"
                  }
                >
                  {typeof row.perPerson === "number"
                    ? row.perPerson.toLocaleString("sv-SE", {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1,
                      })
                    : "—"}
                </td>
                <td>
                  <span
                    className={
                      tone ? `da-verdict da-verdict--${tone}` : "da-verdict"
                    }
                  >
                    {row.tag}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      <h6 className="da-section-title">
        <FormattedMessage id="DailyAnalysisFindings" />
      </h6>
      <FindingList findings={findings} />
    </div>
  );
}

export default DailyAnalysisClient;
